import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useWatch } from "react-hook-form";

import Card from "components/v2/Card/Card";
import type { Organizational } from "api/models/draftRequest.model";
import testIds from "constants/testIds";
import { useGetRequestStartingPoint } from "api/queries/common/useGetRequestStartingPoint";
import { useGetPaymentLocationFormDetails } from "api/queries/location/useGetPaymentLocationFormDetails";
import usePermissions from "hooks/usePermissions";
import Avatar from "components/v2/Avatar/Avatar";
import { useRequestFormDraftId } from "@RequestFormV2/hooks/useRequestFormDraftId";
import { useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import { getPaymentLocationDetailsParams } from "@RequestFormV2/utils/getPaymentLocationDetailsParams";

function ReviewField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="rfv2-review__field">
      <span className="rfv2-review__field-label">{label}</span>
      <span className="rfv2-review__field-value">{value}</span>
    </div>
  );
}

export default function ReviewOrganisation() {
  const intl = useIntl();
  const mode = useRequestFormMode();
  const draftId = useRequestFormDraftId();
  const organizational = useWatch({ name: "organizational" }) as Organizational | undefined;
  const shipmentTitle = useWatch({ name: "shipment.title" }) as string | undefined;
  const location = organizational?.location;
  const paymentLocationParams = getPaymentLocationDetailsParams(mode, location ?? "", draftId);

  const { data: startingPoint } = useGetRequestStartingPoint({});
  const { data: locationDetails } = useGetPaymentLocationFormDetails({
    location: location ?? "",
    config: {
      params: paymentLocationParams.params,
    },
    options: { enabled: !!location },
    isQuote: paymentLocationParams.isQuote,
  });

  const paymentLocationLabel = useMemo(() => {
    if (!location) return undefined;
    return startingPoint?.locations?.find((l) => l.value === location)?.label ?? location;
  }, [location, startingPoint?.locations]);

  const invoiceLocationLabel = useMemo(() => {
    const raw = organizational?.invoice_location;
    if (!raw) return undefined;
    const invoiceField = locationDetails?.organizational_options?.find((f) => f.field_id === "invoice_location");
    const match = invoiceField?.dropdown_values?.find((d) => d.value === raw);
    return match?.key ?? raw;
  }, [organizational?.invoice_location, locationDetails?.organizational_options]);

  const { parsedPermissions } = usePermissions();
  const { company_logo, company_name } = parsedPermissions || {};

  const getOrganizationalFieldLabel = (fieldId: string, fallback: string) =>
    locationDetails?.organizational_options?.find((field) => field.field_id === fieldId)?.label ?? fallback;

  return (
    <Card borderRadius="xl" shadow="xs" data-testid={testIds.REQUEST_FORM_REVIEW_ORGANISATION}>
      <Card.Header
        title={intl.formatMessage({ id: "request-form.organisation", defaultMessage: "Organisation" })}
        showBorder
      />
      <Card.Body>
        <div className="rfv2-review__section-content">
          <div className="d-flex gap-4xl justify-content-between">
            <div className="rfv2-review__shipment-title">{shipmentTitle}</div>
            <div className="d-flex gap-md mr-lg align-items-center">
              <Avatar image={company_logo} name={company_name} size="md" />
              <div>
                <div className="rfv2-review__company-name">{company_name}</div>
                <div className="rfv2-review__payment-location">{paymentLocationLabel}</div>
              </div>
            </div>
          </div>

          {organizational?.custom_long_field_01 && (
            <div className="rfv2-review__field">
              <span className="rfv2-review__field-label">
                {getOrganizationalFieldLabel(
                  "custom_long_field_01",
                  intl.formatMessage({
                    id: "request-form.organisation.long-field-01-description",
                    defaultMessage: "Additional information",
                  }),
                )}
              </span>
              <span className="rfv2-review__field-value rfv2-review__field-value--justified">
                {organizational.custom_long_field_01}
              </span>
            </div>
          )}

          <div className="rfv2-review__field-grid">
            <ReviewField
              label={getOrganizationalFieldLabel(
                "invoice_location",
                intl.formatMessage({
                  id: "request-form.organisation.invoice-location",
                  defaultMessage: "Invoice location",
                }),
              )}
              value={invoiceLocationLabel}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "cost_center",
                intl.formatMessage({
                  id: "request-form.organisation.cost-center",
                  defaultMessage: "Cost center",
                }),
              )}
              value={organizational?.cost_center}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "reason_code",
                intl.formatMessage({
                  id: "request-form.organisation.reason-code",
                  defaultMessage: "Reason code",
                }),
              )}
              value={organizational?.reason_code}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "custom_field_01",
                intl.formatMessage({
                  id: "request-form.organisation.custom-field-01",
                  defaultMessage: "Custom field 1",
                }),
              )}
              value={organizational?.custom_field_01}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "custom_field_02",
                intl.formatMessage({
                  id: "request-form.organisation.custom-field-02",
                  defaultMessage: "Custom field 2",
                }),
              )}
              value={organizational?.custom_field_02}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "custom_field_03",
                intl.formatMessage({
                  id: "request-form.organisation.custom-field-03",
                  defaultMessage: "Custom field 3",
                }),
              )}
              value={organizational?.custom_field_03}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "custom_cost_recovery_field1",
                intl.formatMessage({
                  id: "request-form.organisation.custom-cost-recovery-field1",
                  defaultMessage: "Cost recovery field 1",
                }),
              )}
              value={organizational?.custom_cost_recovery_field1}
            />
            <ReviewField
              label={getOrganizationalFieldLabel(
                "custom_cost_recovery_field2",
                intl.formatMessage({
                  id: "request-form.organisation.custom-cost-recovery-field2",
                  defaultMessage: "Cost recovery field 2",
                }),
              )}
              value={organizational?.custom_cost_recovery_field2}
            />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
